import React, { createContext, useContext, useMemo } from "react";
import type { AppAbilities, AppAbility } from "../config/authzPermissions";
import { buildAbilityFromPairs, createEmptyAbility } from "../config/authzPermissions";
import { defineAbilityFor, Permissions } from "./permissions";

export const AbilityContext = createContext<AppAbility>(createEmptyAbility());

export type Role = keyof typeof Permissions;

type CreateArgs = {
  role?: Role;
  permissions?: { action: string; subject: string }[];
  isSuperAdmin?: boolean;
};

// permission-first; fallback to static role map
export const createAbilityFromAuth = ({ role, permissions, isSuperAdmin }: CreateArgs): AppAbility => {
  if (permissions && permissions.length) {
    return buildAbilityFromPairs(permissions as any, !!isSuperAdmin);
  }
  // fallback: keep your existing static permissions-by-role
  return defineAbilityFor((role as Role) || "guest") as unknown as AppAbility;
};

export const AbilityProvider: React.FC<{ ability: AppAbility; children: React.ReactNode }> = ({ ability, children }) => {
  const memo = useMemo(() => ability, [ability]);
  return <AbilityContext.Provider value={memo}>{children}</AbilityContext.Provider>;
};

export const useAbility = () => useContext(AbilityContext);
